export type ProductCardAutoLinkedField = 'title' | 'description' | 'imageAlt' | 'image';
export type ProductCardFieldOwnership = Partial<Record<ProductCardAutoLinkedField, 'auto' | 'manual'>>;

type ProductCardProductSource = {
  id: number;
  name?: string | null;
  title?: string | null;
  shortDescription?: string | null;
  description?: string | null;
  image?: string | null;
  featuredImage?: string | null;
  imageAlt?: string | null;
  modelPath?: string | null;
  modelId?: number | null;
} | null;

const OWNERSHIP_KEY = 'productFieldOwnership';
const AUTO_LINKED_FIELDS: ProductCardAutoLinkedField[] = [
  'title',
  'description',
  'imageAlt',
  'image',
];

function getProductDefaults(source: ProductCardProductSource) {
  const title = source?.title?.trim() || source?.name || '';
  const description = source?.shortDescription?.trim() || source?.description || '';
  const image = source?.featuredImage?.trim() || source?.image || '';
  return {
    title,
    description,
    imageAlt: source?.imageAlt?.trim() || title || 'Product image',
    image,
  };
}

export function resolveProductCardLinkedFieldValue(
  content: Record<string, unknown>,
  field: ProductCardAutoLinkedField,
  currentSource: ProductCardProductSource
) {
  const ownership = readOwnership(content, currentSource);
  const defaults = getProductDefaults(currentSource);
  if (ownership[field] === 'manual') {
    return typeof content[field] === 'string' ? String(content[field] ?? '') : defaults[field];
  }
  return defaults[field];
}

export function resolveProductCardLinkedFields(
  content: Record<string, unknown>,
  currentSource: ProductCardProductSource
) {
  const ownership = readOwnership(content, currentSource);
  const defaults = getProductDefaults(currentSource);
  return {
    ownership,
    title:
      ownership.title === 'manual'
        ? typeof content.title === 'string'
          ? String(content.title ?? '')
          : defaults.title
        : defaults.title,
    description:
      ownership.description === 'manual'
        ? typeof content.description === 'string'
          ? String(content.description ?? '')
          : defaults.description
        : defaults.description,
    imageAlt:
      ownership.imageAlt === 'manual'
        ? typeof content.imageAlt === 'string'
          ? String(content.imageAlt ?? '')
          : defaults.imageAlt
        : defaults.imageAlt,
    image:
      ownership.image === 'manual'
        ? typeof content.image === 'string'
          ? String(content.image ?? '')
          : defaults.image
        : defaults.image,
  };
}

function readOwnership(
  content: Record<string, unknown>,
  currentSource: ProductCardProductSource
): ProductCardFieldOwnership {
  const explicit =
    content[OWNERSHIP_KEY] && typeof content[OWNERSHIP_KEY] === 'object'
      ? (content[OWNERSHIP_KEY] as Record<string, unknown>)
      : null;
  const defaults = getProductDefaults(currentSource);

  return AUTO_LINKED_FIELDS.reduce<ProductCardFieldOwnership>((accumulator, field) => {
    const explicitValue = explicit?.[field];
    if (explicitValue === 'auto' || explicitValue === 'manual') {
      accumulator[field] = explicitValue;
      return accumulator;
    }

    const currentValue = typeof content[field] === 'string' ? String(content[field] ?? '') : '';
    accumulator[field] =
      currentValue.trim().length === 0 || currentValue === defaults[field] ? 'auto' : 'manual';
    return accumulator;
  }, {});
}

function writeOwnership(
  content: Record<string, unknown>,
  ownership: ProductCardFieldOwnership
) {
  return {
    ...content,
    [OWNERSHIP_KEY]: ownership,
  };
}

export function applyProductCardLinkedProductChange(args: {
  content: Record<string, unknown>;
  currentSource: ProductCardProductSource;
  nextSource: ProductCardProductSource;
}) {
  const { content, currentSource, nextSource } = args;
  const ownership = readOwnership(content, currentSource);
  const defaults = getProductDefaults(nextSource);

  const nextContent: Record<string, unknown> = {
    ...content,
    modelReference: nextSource?.id ?? content.modelReference,
    productId: nextSource?.id ?? content.productId,
    modelPath: nextSource?.modelPath ?? '',
    productModelId:
      nextSource?.modelId ??
      (Number.isFinite(content.productModelId) ? Number(content.productModelId) : content.productModelId),
  };

  AUTO_LINKED_FIELDS.forEach((field) => {
    if (ownership[field] === 'manual') return;
    nextContent[field] = defaults[field];
    ownership[field] = 'auto';
  });

  return writeOwnership(nextContent, ownership);
}

export function applyProductCardManualFieldOverride(
  content: Record<string, unknown>,
  field: ProductCardAutoLinkedField,
  value: string
) {
  const currentSource = Number.isFinite(content.modelReference)
    ? {
        id: Number(content.modelReference),
        name: typeof content.title === 'string' ? content.title : null,
        description: typeof content.description === 'string' ? content.description : null,
        image: typeof content.image === 'string' ? content.image : null,
        modelPath: typeof content.modelPath === 'string' ? content.modelPath : null,
      }
    : null;
  const ownership = readOwnership(content, currentSource);
  ownership[field] = 'manual';
  return writeOwnership(
    {
      ...content,
      [field]: value,
    },
    ownership
  );
}

export function applyProductCardManualImageOverride(
  content: Record<string, unknown>,
  imageUrl: string
) {
  const ownership = readOwnership(content, null);
  ownership.image = 'manual';
  return writeOwnership(
    {
      ...content,
      image: imageUrl,
    },
    ownership
  );
}

export function getProductCardFieldOwnership(
  content: Record<string, unknown>,
  currentSource: ProductCardProductSource
) {
  return readOwnership(content, currentSource);
}

export function applyProductCardResetFieldLink(
  content: Record<string, unknown>,
  field: ProductCardAutoLinkedField,
  currentSource: ProductCardProductSource
) {
  const ownership = readOwnership(content, currentSource);
  const defaults = getProductDefaults(currentSource);
  ownership[field] = 'auto';
  return writeOwnership(
    {
      ...content,
      [field]: defaults[field],
    },
    ownership
  );
}
